import { Flex } from "@chakra-ui/layout";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { TusUpload } from "@intentsify/tus/react";
import { SerializableCsvValidationResults } from "@intentsify/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Endpoints, apiFetchService, apiService } from "api";
import { StatusModal, Upload } from "components";
import { useConfirm } from "components/ConfirmDialog/useConfirm";
import { ReactNode, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  MAX_FILE_SIZE_BYTES,
  getRejectionMessage,
  getUniqueFileName,
  getUploadStatus,
} from "./CsvUploadTus.utils";
import { ValidationResultsModal } from "./ValidationResultsModal";

export type CsvUploadTusProps = {
  tusUpload: TusUpload;
  uploadedFileNames?: string[];
  onFileUpload: (
    tempFileName: string,
    fileName: string,
    validationResults: SerializableCsvValidationResults,
  ) => void;
  validationEndpoint: (tempFileName: string) => string;
  /**
   * Endpoint for the (disabled-by-default) temp-file validation-results query.
   * Defaults to AccountsUpload's route; features gated by a different permission
   * (e.g. ManualReporting → `campaign.manualReporting`) must pass their own so
   * the call doesn't require AccountsUpload's `campaign.edit`.
   */
  tempFileValidationEndpoint?: (tempFileName: string) => string;
  columnsTemplate: Record<string, string>;
  dropzoneSubHeader?: string | ReactNode;
  // Block "Proceed" when any row is invalid (all-or-nothing formats) instead of
  // allowing proceed and failing server-side. Defaults to false (drop-invalid).
  requireAllRowsValid?: boolean;
};

export function CsvUploadTus(props: CsvUploadTusProps) {
  const confirm = useConfirm();
  const toast = useToast();
  const queryClient = useQueryClient();

  const files = props.tusUpload.useFiles();
  const uploadFile = props.tusUpload.useUploadFile();
  const progress = props.tusUpload.useProgress();
  const resumeUploads = props.tusUpload.useResumeUploads();
  const removeFile = props.tusUpload.useRemoveFile();
  const removeAllFiles = props.tusUpload.useRemoveAllFiles();
  const isUploading = props.tusUpload.useIsUploading();
  const hasResumableFiles = props.tusUpload.useHasResumableFiles();

  useEffect(() => {
    const resume = async () => {
      if (
        await confirm({
          title: "Uploading file",
          description: `Uploading a file was interrupted. Would you like to resume uploading?`,
          confirmText: "Resume uploading",
        })
      ) {
        resumeUploads();
      } else {
        removeAllFiles();
      }
    };

    if (hasResumableFiles) {
      void resume();
    }
  }, [confirm, hasResumableFiles, removeAllFiles, resumeUploads]);

  const [fileName, setFileName] = useState<string | undefined>();
  const [tempFileName, setTempFileName] = useState<string | undefined>();
  const [shouldFetchValidation, setShouldFetchValidation] = useState(false);

  const { isFetching: isLoadingValidationResults, data: validationResults } =
    useValidationResults({
      fileName,
      tempFileName,
      tempFileValidationEndpoint: props.tempFileValidationEndpoint,
    });

  const validationModalDisclosure = useDisclosure();

  useEffect(() => {
    if (validationResults && shouldFetchValidation) {
      validationModalDisclosure.onOpen();
    }
  }, [shouldFetchValidation, validationModalDisclosure, validationResults]);

  const dropzone = useDropzone({
    maxFiles: 1,
    multiple: false,
    maxSize: MAX_FILE_SIZE_BYTES,
    accept: { "text/csv": [".csv"] },
    onDropRejected: (rejections) => {
      toast({
        status: "error",
        title: "File not accepted",
        description: getRejectionMessage(rejections),
        isClosable: true,
      });
    },
    onDrop: ([file]) => {
      // react-dropzone calls onDrop even when every file was rejected.
      if (!file) {
        return;
      }

      const fileName = getUniqueFileName(
        file.name,
        props.uploadedFileNames ?? [],
      );

      setFileName(fileName);
      const upload = uploadFile(file, {
        onSuccess() {
          setShouldFetchValidation(true);
        },
        onUploadUrlAvailable() {
          if (!upload.url) {
            throw new Error("Upload URL is not available");
          }

          setTempFileName(upload.url.slice(upload.url.lastIndexOf("/") + 1));
        },
      });
    },
  });

  const fileUploading = files.find((i) => i.status === "uploading");

  const uploadStatus = getUploadStatus({
    progress,
    isLoadingValidationResults,
    hasValidationResults: Boolean(validationResults),
    isUploading,
    shouldFetchValidation,
  });

  const resetState = () => {
    setShouldFetchValidation(false);
    setFileName(undefined);
    setTempFileName(undefined);
    validationModalDisclosure.onClose();
    queryClient.resetQueries(["tempFileValidation", tempFileName]);
  };

  const validationEndpoint = tempFileName
    ? props.validationEndpoint(tempFileName)
    : "";

  useStartValidation(
    {
      fileName,
      tempFileName,
      validationEndpoint,
    },
    { enabled: shouldFetchValidation },
  );

  return (
    <Flex flexDir="column">
      <Upload
        testId="domains-upload-zone"
        subHeader={props.dropzoneSubHeader}
        getInputProps={dropzone.getInputProps}
        getRootProps={dropzone.getRootProps}
      />

      {uploadStatus && (
        <StatusModal
          isOpen={true}
          progressValue={progress.progress}
          status={uploadStatus.status}
          statusSubtitle={uploadStatus.statusSubtitle}
          onExit={
            fileUploading ? () => removeFile(fileUploading.id) : undefined
          }
        />
      )}

      {validationResults?.data && (
        <ValidationResultsModal
          isOpen={validationModalDisclosure.isOpen}
          onCancel={() => {
            resetState();
          }}
          onProceed={() => {
            if (!tempFileName || !fileName) {
              return;
            }

            resetState();

            props.onFileUpload(tempFileName, fileName, validationResults.data);
          }}
          validationResults={validationResults}
          columnsTemplate={props.columnsTemplate}
          requireAllRowsValid={props.requireAllRowsValid}
        />
      )}
    </Flex>
  );
}

function useValidationResults(params: {
  fileName?: string;
  tempFileName?: string;
  tempFileValidationEndpoint?: (tempFileName: string) => string;
}) {
  const result = useQuery({
    queryKey: ["tempFileValidation", params.tempFileName],
    enabled: false,
    queryFn: async () => {
      if (!params.fileName || !params.tempFileName) {
        throw new Error("File name is not available");
      }

      const endpoint =
        params.tempFileValidationEndpoint ??
        Endpoints.AccountsUpload.Get.GetTempFileValidation;

      const res = await apiService<SerializableCsvValidationResults>(
        endpoint(params.tempFileName),
      );

      return { data: res.data, done: true };
    },
  });

  return params.tempFileName ? result : { data: undefined, isFetching: false };
}

function useStartValidation(
  params: {
    fileName?: string;
    tempFileName?: string;
    validationEndpoint?: string;
  },
  options: { enabled?: boolean },
) {
  const queryClient = useQueryClient();

  return useQuery({
    ...options,
    queryKey: ["tempFileValidation", "startValidation", params],
    queryFn: async () => {
      if (
        !params.fileName ||
        !params.tempFileName ||
        !params.validationEndpoint
      ) {
        throw new Error("File name is not available");
      }

      if (!process.env.VITE_REACT_APP_API_URL) {
        throw new Error("API URL is not available");
      }

      const res = await apiFetchService(
        `${process.env.VITE_REACT_APP_API_URL}${params.validationEndpoint}`,
        { credentials: "include" },
      );

      const decoder = new TextDecoder();
      const reader = res.body?.getReader();

      let validationResults;

      while (true) {
        const value = await reader?.read();

        if (value?.done || !value?.value) {
          queryClient.setQueryData(
            ["tempFileValidation", params.tempFileName],
            {
              data: validationResults,
              done: true,
            },
          );
          break;
        }

        const decoded = decoder.decode(value.value);

        if (decoded === "error") {
          throw Error("Internal Server Error");
        }

        try {
          if (decoded !== "ping") {
            validationResults = JSON.parse(
              decoded,
            ) as SerializableCsvValidationResults;

            queryClient.setQueryData(
              ["tempFileValidation", params.tempFileName],
              {
                data: validationResults,
                done: false,
              },
            );
          }
        } catch (error) {
          console.log(error);
        }
      }

      return res.body;
    },
  });
}
